import { stripReportingCategories, stripReportingNames } from '../utils/reportingCategory';

const BASE_URL = process.env.REACT_APP_SQUARE_API || '';
const API_KEY = process.env.REACT_APP_API_KEY || '';

class ApiService {
  constructor(baseUrl) {
    this.baseUrl = baseUrl;
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    
    try {
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': API_KEY,
          ...options.headers,
        },
        ...options,
      });

      if (!response.ok) {
        throw new Error(`API Error: ${response.status} ${response.statusText}`);
      }

      return stripReportingNames(await response.json());
    } catch (error) {
      throw error;
    }
  }

  async get(endpoint, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;
    return this.request(url, { method: 'GET' });
  }

  async post(endpoint, data) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async put(endpoint, data) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' });
  }

  async getCatalogImages(limit = 10, offset = 0, categories = [], sort = 'name', locationIds = [], subcategories = [], sets = [], categoryIds = [], onlineOnly = false, storeOnly = false) {
    const params = { limit, offset, sort, online_only: onlineOnly, store_only: storeOnly };
    if (categoryIds.length > 0) {
      params.category_ids = categoryIds.join(',');
    } else if (categories.length > 0) {
      params.categories = categories.join(',');
    }
    if (subcategories.length > 0) {
      params.subcategories = subcategories.join(',');
    }
    if (sets.length > 0) {
      params.sets = sets.join(',');
    }
    if (locationIds.length > 0) {
      params.location_ids = locationIds.join(',');
    }
    return this.get('catalog/items', params);
  }

  getCatalogItemById(itemId) {
    return this.get(`catalog/items/${itemId}`);
  }

  searchCatalog(query, limit = 25, offset = 0, locationId = null, sort = null, onlineOnly = false, storeOnly = false) {
    const params = { q: query, limit, offset, online_only: onlineOnly, store_only: storeOnly };
    if (locationId) params.location_ids = locationId;
    if (sort) params.sort = sort;
    return this.get('catalog/search', params);
  }

  createCheckout(items) {
    return this.post('checkout/create', { items });
  }

  getInventory(locationId) {
    return this.get('inventory', { locationId });
  }

  getOrder(orderId) {
    return this.get(`orders/${orderId}`);
  }

  getLocations(name = null) {
    return this.get('locations/list', name ? { name } : {});
  }

  getSets() {
    return this.get('catalog/sets');
  }

  getFeaturedItems() {
    return this.get('catalog/items/featured');
  }

  getCategoryTree() {
    return this.get('catalog/categories/tree').then(data => ({
      ...data,
      categories: stripReportingCategories(data.categories),
    }));
  }

  getEventCategories() {
    return this.get('catalog/events/categories');
  }

  getEvents(eventLocationNames = [], includePast = false) {
    const params = { include_past: includePast };
    if (eventLocationNames.length > 0) {
      params.event_location_names = eventLocationNames.join(',');
    }
    return this.get('catalog/events', params);
  }

  // Ask the API which of these cart lines are event registrations. The cart
  // cannot trust its own is_event flag: items persisted in localStorage may
  // predate the flag, so this is the authoritative check.
  resolveEventVariations(variationIds) {
    return this.post('catalog/events/resolve', { variation_ids: variationIds });
  }

  createPaymentLink(cartItems, askForShippingAddress = true, customerId = null, locationId = null, fulfillmentType = null) {
    // Determine location ID: use provided locationId, or fall back to first item's location_id
    let finalLocationId = locationId;
    if (!finalLocationId && cartItems.length > 0) {
      finalLocationId = cartItems[0].location_id;
    }

    if (!finalLocationId) {
      throw new Error('No location ID available for order');
    }

    const lineItems = cartItems.map(item => {
      return {
        quantity: item.quantity.toString(),
        catalog_object_id: item.variation_id,
        item_type: 'ITEM',
        base_price_money: {
          amount: Math.round(item.price * 100),
          currency: 'USD'
        }
      };
    });

    const payload = {
      order: {
        location_id: finalLocationId,
        line_items: lineItems
      },
      ask_for_shipping_address: askForShippingAddress
    };

    if (customerId) {
      payload.customer_id = customerId;
    }
    if (fulfillmentType) {
      payload.fulfillment_type = fulfillmentType;
    }

    return this.post('payments/create-payment-link', payload);
  }

  sendVerificationCode(phoneNumber) {
    return this.post('twilio/verify/send', { phone_number: phoneNumber });
  }

  checkVerificationCode(phoneNumber, code) {
    return this.post('twilio/verify/check', { phone_number: phoneNumber, code });
  }

  searchCustomersByPhone(phoneNumber) {
    return this.post('customers/search/phone', { phone_number: phoneNumber });
  }

  searchLoyaltyAccount(customerId) {
    const params = new URLSearchParams({ customer_id: customerId });
    return this.post(`loyalty/accounts/search?${params.toString()}`, {});
  }
}

const apiService = new ApiService(BASE_URL);

export default apiService;

